import React, { useState, useEffect, useRef, useMemo } from 'react';
import { companyOverrideService } from '../../admin/companies/services/company-override.service';
import {
  OVERRIDABLE_FIELDS,
  OverridableFieldName,
  CompanyFieldOverridePayload,
} from '../../admin/companies/types/override.types';

// Loose structural type — compatible with both AdminCompany and the partner Company type.
// We only require what this component actually reads.
export interface CompanyOverrideShape {
  id: number;
  name?: string;
  description?: string;
  website?: string;
  founding_year?: number | null;
  employees?: string | null;
  industry?: string | null;
  location?: string | null;
  target_market?: string | null;
  traction_stage?: string | null;
  revenue_range?: string | null;
  geographic_focus?: string[] | string | null;
  funding_stage?: string | null;
  total_funding?: number | null;
  is_private?: boolean | null;
  manual_overrides?: Record<string, { overridden_at: string; overridden_by: string }> | null;
  [key: string]: any;
}

interface EnrichmentConflict {
  source: 'linkedin' | 'web';
  sourceLabel: string;
  sourceValue: string;
}

const VALID_EMPLOYEE_RANGES = [
  '1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+',
] as const;

const DROPDOWN_OPTIONS: Partial<Record<OverridableFieldName, readonly string[]>> = {
  employees: VALID_EMPLOYEE_RANGES,
  target_market: ['B2B', 'B2C', 'B2G', 'B2B2C'],
  traction_stage: ['Pre-revenue', 'Seed', 'Growth', 'Scale', 'Mature'],
  revenue_range: ['<100K', '100K-1M', '1M-10M', '10M-50M', '50M+'],
  funding_stage: [
    'Bootstrap', 'Pre-seed', 'Seed', 'Series A', 'Series B', 'Series C', 'Series D+', 'IPO',
  ],
};

function normalizeEmployeeValue(raw: string): string | null {
  const stripped = raw.replace(/\s*employees?\s*/i, '').trim();
  if ((VALID_EMPLOYEE_RANGES as readonly string[]).includes(stripped)) return stripped;
  const match = raw.match(/(\d[\d,]*)\s*[-–]\s*(\d[\d,]*)/);
  if (match) {
    const start = parseInt(match[1].replace(/,/g, ''));
    const ranges = [
      [1, 10], [11, 50], [51, 200], [201, 500], [501, 1000], [1001, 5000],
    ] as const;
    for (const [lo, hi] of ranges) {
      if (start >= lo && start <= hi) return `${lo}-${hi}`;
    }
    if (start > 5000) return '5000+';
  }
  return null;
}

const FIELD_LABELS: Record<OverridableFieldName, string> = {
  name: 'Company Name',
  description: 'Description',
  website: 'Website',
  founding_year: 'Founding Year',
  employees: 'Employees',
  industry: 'Industry',
  location: 'Location',
  target_market: 'Target Market',
  traction_stage: 'Traction Stage',
  revenue_range: 'Revenue Range',
  geographic_focus: 'Geographic Focus',
  funding_stage: 'Funding Stage',
  total_funding: 'Total Funding (USD)',
  is_private: 'Visibility',
};

const LINKEDIN_FIELD_MAP: Partial<Record<OverridableFieldName, string[]>> = {
  name: ['company_name'],
  description: ['about', 'description'],
  industry: ['industry'],
  founding_year: ['founded_year'],
  employees: ['company_size', 'employee_count_on_linkedin'],
  location: ['headquarters'],
  website: ['website'],
};

const WEB_FIELD_MAP: Partial<Record<OverridableFieldName, string[]>> = {
  name: ['company_name'],
  description: ['description', 'about'],
  industry: ['industry'],
  location: ['headquarters_location'],
};

export interface FieldOverrideReviewProps {
  companyId: number;
  company: CompanyOverrideShape;
  onFieldsUpdated: () => void;
  linkedInData?: { extracted_data?: Record<string, any> } | null;
  webEnrichmentData?: { extracted_data?: Record<string, any> } | null;
  /** Primary accent colour (defaults to blue #2563EB) */
  accentColor?: string;
}

export const FieldOverrideReview: React.FC<FieldOverrideReviewProps> = ({
  companyId,
  company,
  onFieldsUpdated,
  linkedInData,
  webEnrichmentData,
  accentColor = '#2563EB',
}) => {
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expandedConflict, setExpandedConflict] = useState<string | null>(null);
  const [expandedOverride, setExpandedOverride] = useState<string | null>(null);

  // Keep a ref that always reflects the latest dirtyFields so the company-reload
  // effect can read the current value without adding dirtyFields as a dependency
  // (which would cause unnecessary re-initialisations on every edit).
  const dirtyFieldsRef = useRef<Set<string>>(dirtyFields);
  useEffect(() => {
    dirtyFieldsRef.current = dirtyFields;
  }, [dirtyFields]);

  // Initialise / merge field values from company.
  // Only fields that are NOT currently dirty are refreshed; in-progress edits
  // for other fields are preserved so that a single-field Discard or "Use this"
  // save does not silently discard unsaved work on sibling fields.
  useEffect(() => {
    const currentDirty = dirtyFieldsRef.current;
    const freshValues: Record<string, any> = {};
    for (const field of OVERRIDABLE_FIELDS) {
      // Preserve the user's in-progress value for any field they are editing.
      if (currentDirty.has(field)) continue;

      const val = (company as any)[field];
      if (field === 'geographic_focus' && Array.isArray(val)) {
        freshValues[field] = val.join(', ');
      } else if (val === null || val === undefined) {
        freshValues[field] = '';
      } else {
        freshValues[field] = String(val);
      }
    }
    setFieldValues((prev) => ({ ...prev, ...freshValues }));
    // Only clear dirty flags for fields that were actually refreshed.
    // Fields the user is still editing stay dirty.
    setDirtyFields((prev) => {
      if (prev.size === 0) return prev; // nothing to do — avoid creating a new Set
      const next = new Set(prev);
      for (const field of OVERRIDABLE_FIELDS) {
        if (!currentDirty.has(field)) next.delete(field);
      }
      return next;
    });
  }, [company]);

  const handleFieldChange = (field: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [field]: value }));
    setDirtyFields((prev) => new Set(prev).add(field));
    setSaveSuccess(false);
  };

  // Conflict detection
  const conflicts = useMemo(() => {
    const result: Record<string, EnrichmentConflict[]> = {};
    const liData = linkedInData?.extracted_data;
    const webData = webEnrichmentData?.extracted_data;

    const checkSource = (
      field: OverridableFieldName,
      storedStr: string,
      data: Record<string, any>,
      keys: string[],
      source: 'linkedin' | 'web',
      sourceLabel: string,
    ): EnrichmentConflict | null => {
      for (const key of keys) {
        const raw = data[key];
        if (raw === null || raw === undefined || String(raw).trim() === '') continue;

        let displayValue = String(raw).trim();
        let compareValue = displayValue.toLowerCase();

        if (field === 'employees') {
          const normalized = normalizeEmployeeValue(displayValue);
          if (!normalized) continue;
          displayValue = normalized;
          compareValue = normalized.toLowerCase();
        }

        if (
          compareValue !== storedStr &&
          !storedStr.includes(compareValue) &&
          !compareValue.includes(storedStr)
        ) {
          return { source, sourceLabel, sourceValue: displayValue };
        }
      }
      return null;
    };

    for (const field of OVERRIDABLE_FIELDS) {
      const stored = (company as any)[field];
      if (stored === null || stored === undefined || String(stored).trim() === '') continue;
      const storedStr = String(stored).trim().toLowerCase();
      const fieldConflicts: EnrichmentConflict[] = [];

      const liKeys = LINKEDIN_FIELD_MAP[field];
      if (liData && liKeys) {
        const c = checkSource(field, storedStr, liData, liKeys, 'linkedin', 'LinkedIn');
        if (c) fieldConflicts.push(c);
      }

      const webKeys = WEB_FIELD_MAP[field];
      if (webData && webKeys) {
        const c = checkSource(field, storedStr, webData, webKeys, 'web', 'Website');
        if (c) fieldConflicts.push(c);
      }

      if (fieldConflicts.length > 0) {
        result[field] = fieldConflicts;
      }
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, linkedInData, webEnrichmentData]);

  const overrides = company.manual_overrides || {};

  // Prepare typed fields payload for saving
  const buildFieldsPayload = (fieldSet: Set<string>): Record<string, any> => {
    const fields: Record<string, any> = {};
    for (const field of Array.from(fieldSet)) {
      const raw = fieldValues[field];
      if (field === 'founding_year' || field === 'total_funding') {
        fields[field] = raw === '' ? null : Number(raw);
      } else if (field === 'is_private') {
        fields[field] = raw === 'true';
      } else if (field === 'geographic_focus') {
        fields[field] =
          raw === ''
            ? null
            : String(raw)
                .split(',')
                .map((s: string) => s.trim())
                .filter(Boolean);
      } else {
        fields[field] = raw;
      }
    }
    return fields;
  };

  const callOverride = async (payload: CompanyFieldOverridePayload) => {
    setIsSubmitting(true);
    try {
      await companyOverrideService.overrideCompanyFields(companyId, payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveOverrides = async () => {
    if (dirtyFields.size === 0) return;
    setSaveError(null);

    try {
      await callOverride({ fields: buildFieldsPayload(dirtyFields) });
      setDirtyFields(new Set());
      setSaveSuccess(true);
      onFieldsUpdated();
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save overrides');
    }
  };

  /**
   * Discard / "Keep mine": lock the current stored value so the enrichment
   * suggestion no longer shows as a conflict. We send the existing field value
   * back to the override endpoint — this marks it as manually overridden,
   * suppressing further conflicts for this field.
   *
   * The field is removed from dirtyFields BEFORE onFieldsUpdated() triggers a
   * company reload, so the merge effect will refresh only that field from the
   * server response and leave all other dirty fields intact.
   */
  const handleDiscard = async (field: OverridableFieldName) => {
    setSaveError(null);
    const singleSet = new Set<string>([field]);
    try {
      await callOverride({ fields: buildFieldsPayload(singleSet) });
      // Remove only the saved field from dirty so the merge effect does not
      // clobber unsaved edits on other fields when the company reloads.
      setDirtyFields((prev) => {
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
      setExpandedConflict(null);
      onFieldsUpdated();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to discard suggestion');
    }
  };

  const renderFieldInput = (field: OverridableFieldName) => {
    const value = fieldValues[field] ?? '';
    const isDirty = dirtyFields.has(field);
    const override = overrides[field];
    // Suppress conflict panel for already-overridden fields — show Overridden badge instead
    const fieldConflicts = override ? undefined : conflicts[field];
    const isExpanded = expandedConflict === field;

    return (
      <div key={field} className="py-3 border-b" style={{ borderColor: '#F3F4F6' }}>
        <div className="flex items-start gap-3">
          {/* Field label */}
          <div className="w-40 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <p className="text-xs font-medium text-gray-700">{FIELD_LABELS[field]}</p>
              {fieldConflicts && (
                <button
                  type="button"
                  onClick={() => setExpandedConflict(isExpanded ? null : field)}
                  className="flex-shrink-0"
                  title="Data conflict detected — click to view"
                >
                  <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Input — focus ring themed to accentColor via cascading CSS var */}
          <div className="flex-1" style={{ '--tw-ring-color': accentColor } as React.CSSProperties}>
            {field === 'description' ? (
              <textarea
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                rows={2}
                className={`w-full px-3 py-1.5 text-sm rounded border focus:outline-none focus:ring-2 resize-none ${
                  isDirty
                    ? 'border-blue-400 bg-blue-50'
                    : fieldConflicts
                    ? 'border-amber-300'
                    : 'border-gray-200'
                }`}
              />
            ) : field === 'is_private' ? (
              <select
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className={`px-3 py-1.5 text-sm rounded border focus:outline-none focus:ring-2 ${
                  isDirty ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <option value="false">Public</option>
                <option value="true">Private</option>
              </select>
            ) : DROPDOWN_OPTIONS[field] ? (
              <select
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className={`px-3 py-1.5 text-sm rounded border focus:outline-none focus:ring-2 ${
                  isDirty
                    ? 'border-blue-400 bg-blue-50'
                    : fieldConflicts
                    ? 'border-amber-300'
                    : 'border-gray-200'
                }`}
              >
                <option value="">Select...</option>
                {DROPDOWN_OPTIONS[field]!.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : field === 'geographic_focus' ? (
              <input
                type="text"
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                placeholder="Kenya, Uganda, Tanzania"
                className={`w-full px-3 py-1.5 text-sm rounded border focus:outline-none focus:ring-2 ${
                  isDirty
                    ? 'border-blue-400 bg-blue-50'
                    : fieldConflicts
                    ? 'border-amber-300'
                    : 'border-gray-200'
                }`}
              />
            ) : field === 'founding_year' || field === 'total_funding' ? (
              <input
                type="number"
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className={`w-40 px-3 py-1.5 text-sm rounded border focus:outline-none focus:ring-2 ${
                  isDirty
                    ? 'border-blue-400 bg-blue-50'
                    : fieldConflicts
                    ? 'border-amber-300'
                    : 'border-gray-200'
                }`}
              />
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className={`w-full px-3 py-1.5 text-sm rounded border focus:outline-none focus:ring-2 ${
                  isDirty
                    ? 'border-blue-400 bg-blue-50'
                    : fieldConflicts
                    ? 'border-amber-300'
                    : 'border-gray-200'
                }`}
              />
            )}
          </div>

          {/* Overridden badge */}
          {override && (
            <div className="flex-shrink-0 mt-1 relative">
              <button
                type="button"
                onClick={() => setExpandedOverride(expandedOverride === field ? null : field)}
                className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 cursor-pointer hover:bg-blue-100 transition-colors"
              >
                Overridden
              </button>
              {expandedOverride === field && (
                <div className="absolute right-0 top-7 z-10 px-3 py-2 rounded-lg bg-white border border-blue-200 shadow-md whitespace-nowrap">
                  <p className="text-xs text-blue-600">Overridden by {override.overridden_by}</p>
                  <p className="text-xs text-blue-500 mt-0.5">
                    {new Date(override.overridden_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Conflict detail panel */}
        {fieldConflicts && isExpanded && (
          <div className="ml-[172px] mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm">
            <p className="text-xs font-medium text-amber-800 mb-2">
              Data mismatch detected for {FIELD_LABELS[field]}
            </p>
            <div className="space-y-1.5">
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-gray-500 w-16 flex-shrink-0">Stored:</span>
                <span className="text-xs font-medium text-gray-900">
                  {String((company as any)[field])}
                </span>
              </div>
              {fieldConflicts.map((c, i) => (
                <div key={i} className="flex items-baseline gap-2">
                  <span className="text-xs text-gray-500 w-16 flex-shrink-0">{c.sourceLabel}:</span>
                  <span className="text-xs font-medium text-amber-900">{c.sourceValue}</span>
                  <button
                    type="button"
                    onClick={() => {
                      handleFieldChange(field, c.sourceValue);
                      setExpandedConflict(null);
                    }}
                    className="ml-auto text-xs text-blue-600 hover:text-blue-800 hover:underline flex-shrink-0"
                  >
                    Use this
                  </button>
                </div>
              ))}
              {/* Discard — lock the current value so the suggestion stops appearing */}
              <div className="pt-1 border-t border-amber-200 mt-1.5 flex justify-end">
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleDiscard(field)}
                  className="text-xs text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-50"
                  title="Keep my current value and stop showing this suggestion"
                >
                  Discard / Keep mine
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Field Overrides</h3>
          <p className="text-xs text-gray-500 mt-1">
            Overriding a field protects it from automated enrichment. Only changed fields are saved.
          </p>
          {Object.keys(conflicts).length > 0 && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
              {Object.keys(conflicts).length} field
              {Object.keys(conflicts).length !== 1 ? 's' : ''} differ from enrichment sources
            </p>
          )}
        </div>
        <button
          onClick={handleSaveOverrides}
          disabled={isSubmitting || dirtyFields.size === 0}
          className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
          style={{ backgroundColor: dirtyFields.size > 0 ? accentColor : '#9CA3AF' }}
        >
          {isSubmitting
            ? 'Saving...'
            : saveSuccess
            ? 'Saved!'
            : `Save Overrides (${dirtyFields.size})`}
        </button>
      </div>

      {saveError && (
        <p className="text-sm mb-3" style={{ color: '#EF4444' }}>
          {saveError}
        </p>
      )}
      {saveSuccess && (
        <p className="text-sm mb-3 text-green-600">Changes saved successfully.</p>
      )}

      <div className="bg-white rounded-lg">
        {OVERRIDABLE_FIELDS.map((field) => renderFieldInput(field))}
      </div>
    </div>
  );
};
